// ---------------------------------------------------------
// PatDagEditor.jsx — stable DAG editor with undo / redo
// ---------------------------------------------------------

import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

import ReactFlow, {
  Background,
  Controls,
  applyEdgeChanges,
  applyNodeChanges,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";

import "reactflow/dist/style.css";

// ─────────────────────────────────────────────
// React Flow static type registries (REQUIRED)
// ─────────────────────────────────────────────
const EDGE_TYPES = {};

function isCoarsePointerDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  return !!(
    window.matchMedia?.("(pointer: coarse)")?.matches ||
    window.matchMedia?.("(hover: none)")?.matches ||
    navigator.maxTouchPoints > 0
  );
}

/* ---------------------------------------------------------
   Editable node
--------------------------------------------------------- */
function EditableNode({ data }) {
  const locked = !!data.locked;

  const [text, setText] = useState(
    data.displayLabel || data.canonicalLabel || "",
  );
  const [role, setRole] = useState(data.role || "X");
  const [showLabelTooltip, setShowLabelTooltip] = useState(false);

  useEffect(() => {
    setText(data.displayLabel || data.canonicalLabel || "");
  }, [data.displayLabel, data.canonicalLabel]);

  useEffect(() => {
    setRole(data.role || "X");
  }, [data.role]);

  const isTouch = !!data.isTouchDevice;
  const fullLabel = String(
    text || data.displayLabel || data.canonicalLabel || "",
  );
  const shouldShowLabelTooltip =
    showLabelTooltip && fullLabel.trim().length > 0;
  const handleSize = isTouch ? 34 : 22;
  const handleBorder = isTouch ? "4px solid #020617" : "3px solid #020617";
  const targetPosition =
    data.role === "Y"
      ? isTouch
        ? Position.Right
        : Position.Top
      : Position.Left;

  const updateLabel = (value) => {
    setText(value);
    data.onLabelChange?.(value);
  };

  const updateRole = (value) => {
    setRole(value);
    data.onRoleChange?.(value);
  };

  return (
    <div
      style={{
        position: "relative",
        padding: isTouch ? "8px 10px" : "8px 12px",
        background:
          data.role === "Y"
            ? "#1f2937"
            : data.origin === "user"
              ? "#1e2537"
              : "#1e293b",
        borderRadius: data.role === "Y" ? "50%" : "8px",
        minWidth:
          data.role === "Y"
            ? isTouch
              ? "112px"
              : "140px"
            : isTouch
              ? "148px"
              : "180px",
        height: data.role === "Y" ? (isTouch ? "112px" : "140px") : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        border:
          data.role === "Y"
            ? "3px solid #38bdf8"
            : data.origin === "user"
              ? "2px dashed #94a3b8"
              : "1px solid #334155",
        boxShadow:
          data.role === "Y"
            ? "0 0 0 2px rgba(56,189,248,0.35)"
            : data.origin === "user"
              ? "0 0 0 1px rgba(148,163,184,0.15) inset"
              : "none",
        color: "white",
      }}
    >
      {!locked &&
        data.nodeDeletionEnabled &&
        typeof data.onDelete === "function" && (
          <button
            type="button"
            className="nodrag nopan"
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Delete variable"
            aria-label="Delete variable"
            style={{
              position: "absolute",
              top: isTouch ? "-12px" : "-10px",
              right: isTouch ? "-12px" : "-10px",
              zIndex: 20,
              width: isTouch ? "30px" : "24px",
              height: isTouch ? "30px" : "24px",
              borderRadius: "999px",
              border: "1px solid #64748b",
              background: "#020617",
              color: "#cbd5e1",
              fontSize: isTouch ? "18px" : "14px",
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}

      <Handle
        type="target"
        position={targetPosition}
        style={{
          width: handleSize,
          height: handleSize,
          borderRadius: "50%",
          background: "#94a3b8",
          border: handleBorder,
          cursor: "crosshair",
        }}
      />

      {data.role !== "Y" && (
        <Handle
          type="source"
          position={Position.Right}
          style={{
            width: handleSize,
            height: handleSize,
            borderRadius: "50%",
            background: "#38bdf8",
            border: handleBorder,
            cursor: "crosshair",
          }}
        />
      )}

      {/* Label */}
      <div
        className="nodrag nopan"
        style={{
          position: "relative",
          width: "100%",
          marginBottom: data.role === "Y" ? "0px" : "6px",
        }}
        onMouseEnter={() => setShowLabelTooltip(true)}
        onMouseLeave={() => setShowLabelTooltip(false)}
        onFocus={() => setShowLabelTooltip(true)}
        onBlur={() => setShowLabelTooltip(false)}
      >
        <input
          className="nodrag nopan"
          value={text}
          disabled={false}
          aria-label={
            fullLabel ? `Variable label: ${fullLabel}` : "Variable label"
          }
          onChange={(e) => updateLabel(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            textAlign: "center",
            color: "white",
            outline: "none",
            fontSize: isTouch ? "14px" : "16px",
          }}
        />

        {shouldShowLabelTooltip && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: "calc(100% + 8px)",
              transform: "translateX(-50%)",
              zIndex: 50,
              maxWidth: isTouch ? "220px" : "320px",
              padding: "6px 8px",
              borderRadius: "8px",
              border: "1px solid #475569",
              background: "#020617",
              color: "#e2e8f0",
              fontSize: isTouch ? "12px" : "13px",
              lineHeight: 1.35,
              whiteSpace: "normal",
              boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
              pointerEvents: "none",
            }}
          >
            {fullLabel}
          </div>
        )}
      </div>

      {/* Role selector */}
      <select
        className="nodrag nopan"
        value={role}
        disabled={locked}
        onChange={(e) => updateRole(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        title={locked ? "Node is locked after DAG confirmation" : ""}
        onPointerDown={(e) => {
          // ReactFlow drag capture happens on pointer events, not mouse events
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          width: "100%",
          background: "#020617",
          color: "#e5e7eb",
          border: "1px solid #475569",
          borderRadius: "6px",
          fontSize: isTouch ? "13px" : "14px",
          padding: isTouch ? "5px 6px" : "6px 8px",
          minHeight: isTouch ? "32px" : "34px",
          cursor: locked ? "not-allowed" : "pointer",
        }}
      >
        <option value="X">X — Candidate X</option>
        <option value="Y">Y — Outcome</option>
        <option value="M">M — Mediator</option>
        <option value="Z">Z — Confounder</option>
      </select>
    </div>
  );
}
const NODE_TYPES = {
  editableNode: EditableNode,
};

// ---------------------------------------------------------
// Role-based deterministic layout (prevents overlap on role change)
// ---------------------------------------------------------
function layoutNodesByRole(nodes) {
  const touch = isCoarsePointerDevice();
  const GAP_Y = touch ? 150 : 190;
  const BASE_Y = touch ? 50 : 70;
  const COL_PADDING = touch ? 24 : 40;
  const BASE_X = touch ? 20 : 40;

  // Estimate width from label length (ReactFlow-safe heuristic)
  const estimateWidth = (label = "") =>
    touch
      ? Math.min(220, Math.max(150, label.length * 6 + 44))
      : Math.min(320, Math.max(200, label.length * 7 + 60));

  // Group system nodes by role
  const byRole = { X: [], M: [], Z: [], Y: [] };
  nodes.forEach((n) => {
    if (n?.data?.origin === "system") {
      byRole[n.data.role || "X"]?.push(n);
    }
  });

  // Compute column widths
  const colWidths = {};
  Object.keys(byRole).forEach((role) => {
    colWidths[role] =
      Math.max(
        ...byRole[role].map((n) => estimateWidth(n.data?.displayLabel)),
        180,
      ) + COL_PADDING;
  });

  // Compute cumulative X positions
  const colX = {};
  let cursorX = BASE_X;
  ["X", "M", "Z", "Y"].forEach((role) => {
    colX[role] = cursorX;
    cursorX += colWidths[role] || 220;
  });

  // Stack vertically within each column
  const counters = { X: 0, M: 0, Z: 0 };

  return nodes.map((n) => {
    const role = n?.data?.role || "X";

    // Do not reposition outcome vertically
    if (role === "Y") {
      return {
        ...n,
        position: {
          x: colX.Y,
          y: n.position?.y ?? 200,
        },
      };
    }

    // Never move user nodes
    if (n?.data?.origin === "user") return n;

    const index = counters[role]++;

    const tallest =
      Math.max(byRole.X.length, byRole.M.length, byRole.Z.length, 1) * GAP_Y;

    const colHeight = (byRole[role]?.length || 1) * GAP_Y;
    const offset = (tallest - colHeight) / 2;

    return {
      ...n,
      position: {
        x: colX[role],
        y: BASE_Y + offset + index * GAP_Y,
      },
    };
  });
}

/* ---------------------------------------------------------
   Cycle detection
--------------------------------------------------------- */

function createsCycle(source, target, edges) {
  if (source === target) return true;

  const adj = {};
  edges.forEach((e) => {
    (adj[e.source] ||= []).push(e.target);
  });

  const stack = [target];
  const visited = new Set();

  while (stack.length) {
    const node = stack.pop();
    if (node === source) return true;
    (adj[node] || []).forEach((n) => {
      if (!visited.has(n)) {
        visited.add(n);
        stack.push(n);
      }
    });
  }

  return false;
}

/* ---------------------------------------------------------
   Main component
--------------------------------------------------------- */

function PatDagEditorInner({
  predictors = [],
  outcome,
  initialGraph,
  onGraphChange,
  locked: lockedProp = false,
}) {
  const locked = !!lockedProp;
  const { fitView } = useReactFlow();
  const isTouchDevice = useMemo(() => isCoarsePointerDevice(), []);
  const [nodeDeletionEnabled, setNodeDeletionEnabled] = useState(false);

  const canEditDag = !locked;
  const canDeleteNodes = canEditDag && nodeDeletionEnabled;

  useEffect(() => {
    if (locked) {
      setNodeDeletionEnabled(false);
    }
  }, [locked]);

  /* ------------------------------
     Build initial nodes ONCE
  ------------------------------ */
  const [nodes, setNodes] = useState(() => {
    // ────────────────────────────────
    // Authoritative hydration (confirmed DAG)
    // ────────────────────────────────
    if (
      locked &&
      Array.isArray(initialGraph?.nodes) &&
      initialGraph.nodes.length
    ) {
      return initialGraph.nodes.map((n) => ({
        id: n.id,
        type: "editableNode",
        draggable: !locked,
        position: n.position || { x: 100, y: 100 },
        data: {
          locked,
          isTouchDevice,
          canonicalLabel: n.label,
          displayLabel: n.displayLabel || n.label,
          role: n.role || "X",
          origin: "system",
          onLabelChange: (displayLabel) =>
            setNodes((nds) =>
              nds.map((node) =>
                node.id === n.id
                  ? {
                      ...node,
                      data: {
                        ...node.data,
                        displayLabel,
                        canonicalLabel: displayLabel,
                      },
                    }
                  : node,
              ),
            ),
          onRoleChange: () => {},
        },
      }));
    }

    // ────────────────────────────────
    // Pre-confirm construction mode
    // ────────────────────────────────
    if (!predictors.length && !outcome) return [];

    const n = [];
    const xCause = 40;
    const xMediator = 280;
    const xConfounder = 520;
    const xOutcome = 800;

    const causes = predictors.filter((p) => p.role === "X");
    const mediators = predictors.filter((p) => p.role === "M");
    const confounders = predictors.filter((p) => p.role === "Z");

    const stack = (arr, x) =>
      arr.forEach((p, i) => {
        n.push({
          id: p.id,
          type: "editableNode",
          draggable: true,
          position: { x, y: 80 + i * 120 },
          data: {
            locked,
            isTouchDevice,
            nodeDeletionEnabled: canDeleteNodes,
            canonicalLabel: p.label,
            displayLabel: p.label,
            role: p.role ?? "X",
            origin: "system",
            onLabelChange: (displayLabel) =>
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === p.id
                    ? {
                        ...node,
                        data: {
                          ...node.data,
                          displayLabel,
                          canonicalLabel: displayLabel,
                        },
                      }
                    : node,
                ),
              ),
            onRoleChange: (role) => updateNodeRole(p.id, role),
            onDelete: () => deleteNode(p.id),
          },
        });
      });

    stack(causes, xCause);
    stack(mediators, xMediator);
    stack(confounders, xConfounder);

    if (outcome) {
      n.push({
        id: outcome.id,
        type: "editableNode",
        draggable: true,
        position: { x: xOutcome, y: 200 },
        data: {
          locked,
          isTouchDevice,
          nodeDeletionEnabled: canDeleteNodes,
          canonicalLabel: outcome.label,
          displayLabel: outcome.label,
          role: "Y",
          origin: "system",
          onLabelChange: (displayLabel) =>
            setNodes((nds) =>
              nds.map((node) =>
                node.id === outcome.id
                  ? {
                      ...node,
                      data: {
                        ...node.data,
                        displayLabel,
                        canonicalLabel: displayLabel,
                      },
                    }
                  : node,
              ),
            ),
          onRoleChange: (role) => updateNodeRole(outcome.id, role),
          onDelete: () => deleteNode(outcome.id),
        },
      });
    }

    return layoutNodesByRole(n);
  });

  /* ------------------------------
     Build initial edges ONCE
  ------------------------------ */
  const [edges, setEdges] = useState(() => {
    if (!Array.isArray(initialGraph?.edges)) return [];

    return initialGraph.edges
      .map((e, i) => {
        if (Array.isArray(e) && e.length >= 2) {
          return { id: `e-${i}`, source: e[0], target: e[1] };
        }
        if (e && typeof e === "object") {
          const source = e.source ?? e.from;
          const target = e.target ?? e.to;
          if (source && target) {
            return { id: `e-${i}`, source, target };
          }
        }
        return null;
      })
      .filter(Boolean);
  });

  const nodesRef = useRef([]);
  const edgesRef = useRef([]);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  const [roleWarning, setRoleWarning] = useState("");
  const [hasRoleViolation, setHasRoleViolation] = useState(false);

  // ------------------------------
  // FIX: sync edges when initialGraph changes
  // ------------------------------
  useEffect(() => {
    if (!locked) return;
    if (!Array.isArray(initialGraph?.edges)) return;

    const rebuiltEdges = initialGraph.edges
      .map((e, i) => {
        if (Array.isArray(e) && e.length >= 2) {
          return { id: `e-${i}`, source: e[0], target: e[1] };
        }
        if (e && typeof e === "object") {
          const source = e.source ?? e.from;
          const target = e.target ?? e.to;
          if (source && target) {
            return { id: `e-${i}`, source, target };
          }
        }
        return null;
      })
      .filter(Boolean);

    setEdges(rebuiltEdges);
  }, [initialGraph]);

  const updateNodeRole = useCallback((id, newRole) => {
    setNodes((prev) => {
      const existingY = prev.find((n) => n?.data?.role === "Y" && n.id !== id);

      if (newRole === "Y" && existingY) {
        setRoleWarning(
          "A focal outcome (Y) has already been nominated. PAT permits exactly one Y per model. Reassign or remove the existing Y before continuing.",
        );
        return prev;
      }

      setRoleWarning("");

      const next = prev.map((n) => {
        if (n.id === id) {
          return { ...n, data: { ...n.data, role: newRole } };
        }
        return n;
      });

      return layoutNodesByRole(next);
    });
  }, []);

  // ─────────────────────────────────────────
  // Hydrate nodes ONLY in confirmed mode
  // ─────────────────────────────────────────
  useEffect(() => {
    if (!locked) return;
    if (!Array.isArray(initialGraph?.nodes)) return;

    const rebuiltNodes = initialGraph.nodes.map((n) => ({
      id: n.id,
      type: "editableNode",
      draggable: !locked,
      position: n.position || { x: 100, y: 100 },
      data: {
        locked,
        isTouchDevice,
        nodeDeletionEnabled: canDeleteNodes,
        canonicalLabel: n.label,
        displayLabel: n.displayLabel || n.label,
        role: n.role || "X",
        origin: "system",
        onLabelChange: (displayLabel) =>
          setNodes((nds) =>
            nds.map((node) =>
              node.id === n.id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      displayLabel,
                      canonicalLabel: displayLabel,
                    },
                  }
                : node,
            ),
          ),
        onRoleChange: (newRole) => updateNodeRole(n.id, newRole),
      },
    }));

    setNodes(rebuiltNodes);
  }, [locked, initialGraph, updateNodeRole, canDeleteNodes]);

  /* ------------------------------
     Sync nodes when predictors / outcome change
     SNAP-TO-GRID (production mode)
     - System nodes ALWAYS reflow deterministically
     - User-added nodes are preserved
  ------------------------------ */
  const lastSigRef = useRef("");

  useEffect(() => {
    const sig = JSON.stringify({
      predictors: predictors.map((p) => ({
        id: p.id,
        label: p.label,
        role: p.role ?? "X",
      })),
      outcome: outcome ? { id: outcome.id, label: outcome.label } : null,
    });

    // Hard guard: no structural change → do nothing
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;

    setNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));

      const next = [];

      // ─────────────────────────────────────────
      // Layout constants (stable, conservative)
      // ─────────────────────────────────────────
      const BASE_X = isTouchDevice ? 24 : 80;
      const BASE_Y = isTouchDevice ? 60 : 90;
      const GAP_Y = isTouchDevice ? 132 : 160;
      const COL_GAP = isTouchDevice ? 96 : 240;

      const estimateWidth = (label = "") =>
        isTouchDevice
          ? Math.min(220, Math.max(150, label.length * 6 + 56))
          : Math.min(420, Math.max(240, label.length * 8 + 100));
      // ─────────────────────────────────────────
      // Partition predictors by role
      // ─────────────────────────────────────────
      const effectivePredictors =
        !predictors.length && outcome
          ? [
              {
                id: "X-placeholder",
                label: "Candidate X",
                role: "X",
              },
            ]
          : predictors;

      const byRole = { X: [], M: [], Z: [] };
      effectivePredictors.forEach((p) => {
        const role = p.role ?? "X";
        if (byRole[role]) byRole[role].push(p);
      });

      const minColWidth = isTouchDevice ? 160 : 260;
      const wX = Math.max(
        ...byRole.X.map((p) => estimateWidth(p.label)),
        minColWidth,
      );
      const wM = Math.max(
        ...byRole.M.map((p) => estimateWidth(p.label)),
        minColWidth,
      );
      const wZ = Math.max(
        ...byRole.Z.map((p) => estimateWidth(p.label)),
        minColWidth,
      );

      const columns = [];

      if (byRole.X.length) columns.push("X");
      if (byRole.M.length) columns.push("M");
      if (byRole.Z.length) columns.push("Z");

      if (outcome) columns.push("Y");

      const colX = {};
      let cursor = BASE_X;

      columns.forEach((role) => {
        colX[role] = cursor;

        const width =
          role === "X"
            ? wX
            : role === "M"
              ? wM
              : role === "Z"
                ? wZ
                : isTouchDevice
                  ? 160
                  : 260;

        cursor += width + COL_GAP;
      });
      if (!colX.Y && outcome) {
        colX.Y = cursor;
      }

      const counters = { X: 0, M: 0, Z: 0 };

      // ─────────────────────────────────────────
      // Reconcile system predictors (X / M / Z)
      // ─────────────────────────────────────────
      effectivePredictors.forEach((p) => {
        const role = p.role ?? "X";
        const index = counters[role]++;
        const prevNode = prevById.get(p.id);

        next.push({
          id: p.id,
          type: "editableNode",
          draggable: true,
          position: {
            x: colX[role],
            y:
              BASE_Y +
              index * GAP_Y +
              (Math.max(byRole.X.length, byRole.M.length, byRole.Z.length, 1) *
                GAP_Y -
                (byRole[role]?.length || 1) * GAP_Y) /
                2,
          },

          data: {
            locked,
            isTouchDevice,
            nodeDeletionEnabled: canDeleteNodes,
            canonicalLabel: p.label,
            displayLabel: prevNode?.data?.displayLabel ?? p.label,
            role,
            origin: "system",
            onLabelChange: (displayLabel) =>
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === p.id
                    ? {
                        ...n,
                        data: {
                          ...n.data,
                          displayLabel,
                          canonicalLabel: displayLabel,
                        },
                      }
                    : n,
                ),
              ),
            onRoleChange: (r) => updateNodeRole(p.id, r),
            onDelete: () => deleteNode(p.id),
          },
        });
      });

      // ─────────────────────────────────────────
      // Reconcile outcome (Y) — single anchor
      // ─────────────────────────────────────────
      if (outcome) {
        const prevNode = prevById.get(outcome.id);

        next.push({
          id: outcome.id,
          type: "editableNode",
          draggable: true,
          position: {
            x: colX.M ?? colX.X ?? BASE_X,
            y:
              BASE_Y +
              Math.max(byRole.X.length, byRole.M.length, byRole.Z.length) *
                GAP_Y +
              140,
          },

          data: {
            locked,
            isTouchDevice,
            nodeDeletionEnabled: canDeleteNodes,
            canonicalLabel: outcome.label,
            displayLabel: prevNode?.data?.displayLabel ?? outcome.label,
            role: "Y",
            origin: "system",
            onLabelChange: (displayLabel) =>
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === outcome.id
                    ? {
                        ...n,
                        data: {
                          ...n.data,
                          displayLabel,
                          canonicalLabel: displayLabel,
                        },
                      }
                    : n,
                ),
              ),
            onRoleChange: (r) => updateNodeRole(outcome.id, r),
            onDelete: () => deleteNode(outcome.id),
          },
        });
      }

      // ─────────────────────────────────────────
      // Append user nodes unchanged
      // ─────────────────────────────────────────
      prev.forEach((n) => {
        if (n?.data?.origin === "user") next.push(n);
      });

      return next.map((n) => ({
        ...n,
        position: {
          x: Number.isFinite(n.position?.x) ? n.position.x : BASE_X,
          y: Number.isFinite(n.position?.y) ? n.position.y : BASE_Y,
        },
      }));
    });
  }, [predictors, outcome, updateNodeRole, locked, canDeleteNodes]);

  /* ------------------------------
     Undo / Redo stacks
  ------------------------------ */
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  useEffect(() => {
    if (!nodes || nodes.length === 0) return;

    requestAnimationFrame(() => {
      fitView({
        padding: 0.5,
        duration: 0,
        includeHiddenNodes: true,
      });
    });
  }, [nodes, fitView]);

  // ------------------------------
  // Stop page scroll while interacting with DAG
  // (window listener + capture phase to defeat stopPropagation)
  // ------------------------------
  const flowWrapRef = useRef(null);

  useEffect(() => {
    const inDag = (target) => {
      const el = flowWrapRef.current;
      return el && target instanceof Node && el.contains(target);
    };

    const onWheel = (e) => {
      if (!inDag(e.target)) return;

      // allow browser zoom shortcuts to behave normally
      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
    };

    const onTouchMove = (e) => {
      if (!inDag(e.target)) return;
      e.preventDefault();
    };

    // capture:true ensures we see the event even if ReactFlow stops propagation
    window.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true,
    });

    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
    };
  }, []);

  /* ------------------------------
     Handlers
  ------------------------------ */
  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => {
        const next = applyEdgeChanges(changes, eds);

        if (next.length < eds.length) {
          undoStack.current.push({ nodes, edges: eds });
          redoStack.current = [];
        }

        return next;
      });
    },
    [nodes, edges],
  );

  const onConnect = useCallback(
    ({ source, target }) => {
      if (locked) return;
      if (!source || !target) return;
      if (edges.some((e) => e.source === source && e.target === target)) return;
      if (createsCycle(source, target, edges)) return;

      undoStack.current.push({ nodes, edges });
      redoStack.current = [];

      setEdges((eds) => [
        ...eds,
        { id: `${source}-${target}`, source, target },
      ]);
    },
    [edges, locked],
  );

  /* ------------------------------
     Emit graph upward
  ------------------------------ */
  useEffect(() => {
    if (!onGraphChange) return;

    const emittedGraph = {
      nodes: nodes.map((n) => ({
        id: n.id,
        label: n.data.canonicalLabel,
        displayLabel: n.data.displayLabel,
        role: n.data.role || "X",
        position: n.position,
      })),
      edges: edges.map((e) => [e.source, e.target]),
    };

    onGraphChange(emittedGraph);
  }, [nodes, edges, roleWarning, onGraphChange]);

  /* ------------------------------
     Undo / Redo
  ------------------------------ */
  const undo = () => {
    if (locked) return;
    const prev = undoStack.current.pop();
    if (prev) {
      redoStack.current.push({ nodes, edges });
      setNodes(prev.nodes);
      setEdges(prev.edges);
    }
  };

  const redo = () => {
    if (locked) return;
    const next = redoStack.current.pop();
    if (next) {
      undoStack.current.push({ nodes, edges });
      setNodes(next.nodes);
      setEdges(next.edges);
    }
  };

  const deleteNode = useCallback(
    (id) => {
      if (!canDeleteNodes || !id) return;

      const currentNodes = nodesRef.current || [];
      const currentEdges = edgesRef.current || [];

      const nodeExists = currentNodes.some((n) => n.id === id);
      if (!nodeExists) return;

      undoStack.current.push({
        nodes: currentNodes,
        edges: currentEdges,
      });
      redoStack.current = [];

      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    },
    [canDeleteNodes],
  );

  useEffect(() => {
    setNodes((prev) =>
      prev.map((node) => {
        const nextDraggable = canEditDag;
        const nextDeletionEnabled = canDeleteNodes;

        if (
          node.draggable === nextDraggable &&
          node.data?.locked === locked &&
          node.data?.nodeDeletionEnabled === nextDeletionEnabled
        ) {
          return node;
        }

        return {
          ...node,
          draggable: nextDraggable,
          data: {
            ...node.data,
            locked,
            nodeDeletionEnabled: nextDeletionEnabled,
            onDelete: () => deleteNode(node.id),
          },
        };
      }),
    );
  }, [locked, canEditDag, canDeleteNodes, deleteNode]);

  const addNode = (label = "New variable") => {
    const id = `user-${Date.now()}`;

    undoStack.current.push({ nodes, edges });
    redoStack.current = [];

    setNodes((nds) => [
      ...nds,
      {
        id,
        type: "editableNode",
        draggable: true,
        position: { x: 200, y: 200 },
        data: {
          locked,
          isTouchDevice,
          nodeDeletionEnabled: canDeleteNodes,
          canonicalLabel: label,
          displayLabel: label,
          role: "Z",
          origin: "user",
          onLabelChange: (displayLabel) =>
            setNodes((nds) =>
              nds.map((n) =>
                n.id === id
                  ? {
                      ...n,
                      data: {
                        ...n.data,
                        displayLabel,
                        canonicalLabel: displayLabel,
                      },
                    }
                  : n,
              ),
            ),
          onRoleChange: (role) => updateNodeRole(id, role),
          onDelete: () => deleteNode(id),
        },
      },
    ]);
  };

  /* ------------------------------
     Render
  ------------------------------ */
  return (
    <div className="w-full border border-slate-700">
      <div className="flex justify-end gap-2 px-2 pb-2">
        <button
          onClick={() => addNode()}
          disabled={locked}
          className="px-3 py-1 text-sm rounded bg-slate-600 text-white disabled:opacity-40"
        >
          Add variable
        </button>
      </div>

      {roleWarning ? (
        <div className="mx-2 mb-2 rounded border border-red-500 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {roleWarning}
        </div>
      ) : null}

      <div
        ref={flowWrapRef}
        className={
          isTouchDevice ? "h-[58vh] min-h-[420px]" : "h-[70vh] min-h-[600px]"
        }
      >
        <ReactFlow
          nodes={nodes}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          deleteKeyCode={canDeleteNodes ? ["Backspace", "Delete"] : null}
          nodesDraggable={canEditDag}
          nodesConnectable={canEditDag}
          elementsSelectable={canEditDag}
          nodeDragThreshold={isTouchDevice ? 14 : 6}
          selectionOnDrag={isTouchDevice ? false : true}
          snapToGrid={true}
          snapGrid={[20, 20]}
          panOnDrag={isTouchDevice ? false : true}
          panOnScroll={false}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={false}
        >
          <Background variant="dots" gap={16} size={1} color="#475569" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <div className="mt-2 flex justify-end gap-2 px-2">
        <button
          type="button"
          onClick={() => setNodeDeletionEnabled((value) => !value)}
          disabled={locked}
          className={`px-3 py-1 text-sm rounded border disabled:opacity-40 ${
            nodeDeletionEnabled
              ? "bg-red-900/40 border-red-500 text-red-200"
              : "bg-slate-700 border-slate-600 text-white"
          }`}
          title={
            nodeDeletionEnabled
              ? "Node deletion is enabled"
              : "Node deletion is disabled"
          }
        >
          Node deletion: {nodeDeletionEnabled ? "On" : "Off"}
        </button>

        <button
          onClick={undo}
          disabled={locked || undoStack.current.length === 0}
          className="px-3 py-1 text-sm rounded bg-slate-700 text-white disabled:opacity-40"
        >
          Undo
        </button>

        <button
          onClick={redo}
          disabled={locked || redoStack.current.length === 0}
          className="px-3 py-1 text-sm rounded bg-slate-700 text-white disabled:opacity-40"
        >
          Redo
        </button>
      </div>
    </div>
  );
}
export default function PatDagEditor(props) {
  return (
    <ReactFlowProvider>
      <PatDagEditorInner {...props} />
    </ReactFlowProvider>
  );
}
